import { isObjectLike } from 'lodash'
import React from 'react'
import Scrollbars from 'react-custom-scrollbars'
import { Trans, withTranslation, WithTranslation } from 'react-i18next'
import { FiSearch, FiX } from 'react-icons/fi'
import {
  ITagsForEvents,
  ITagsForEventsWithPreferences,
} from '../../../reducers/userObject/tags'
import { IFormElementProps } from '../FormElement/FormInput'
import Header from './Header'
import ListTag from './ListTag'
import SearchInput from './SearchInput'
import SearchWrapper from './SearchWrapper'
import StyledInput from './SyledInput'
import TagRemoveWrapper from './TagRemoveWrapper'
import Tags from './Tags'
import TagsList from './TagsList'
import TagWrapper from './TagWrapper'
import TextTag from './TextTag'
import Wrapper, { LockWrapper } from './Wrapper'
import { mapStringToTags } from './index'
import DisabledIcon2 from '../../../assets/disabled.png'

function TagInput({
  tagsList,
  value,
  onChange,
  size = 1,
  className,
  t,
  disabled = false,
}: ITagInputProps & ITagInputDispatch & WithTranslation) {
  const [inputValue, setInputValue] = React.useState('')
  const [search, setSearch] = React.useState('')
  const [focus, setFocus] = React.useState(true)
  const [edit, setEdit] = React.useState(-1)
  const [editValue, setEditValue] = React.useState('')
  const [blurTimeout, setBlurTimeout] = React.useState()
  const [caretPos, setCaretPos] = React.useState(0)
  const inputRef: React.MutableRefObject<HTMLInputElement> = React.useRef<
    HTMLInputElement
  >()

  const tags = mapStringToTags(value, tagsList)

  function formatInput(input: string) {
    return input
      .split(' ')
      .map(tag => (tag in tagsList ? { ...tagsList[tag], key: tag } : tag))
  }

  function addTag(tag: ITag) {
    return function() {
      if (inputValue !== '' && edit === -1) {
        onChange([...tags, ...formatInput(inputValue), tag])
        setInputValue('')
      } else if (edit !== -1) {
        const val = `${editValue.slice(0, caretPos)} ${
          tag.key
        } ${editValue.slice(caretPos)}`

        const tagsCopy = [...tags]

        tagsCopy.splice(edit, 1, ...formatInput(val))

        onChange(tagsCopy)
        setEdit(-1)
        setEditValue('')

        setBlurTimeout(clearTimeout(blurTimeout) as any)
      } else {
        onChange([...tags, tag])
      }
    }
  }

  function removeTag(index: number) {
    return function() {
      const tagsCopy = [...tags]
      tagsCopy.splice(index, 1)
      onChange(tagsCopy)
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter') {
      // Add tag if user inserted a value and no other tags are in edit
      if (inputValue !== '' && edit === -1) {
        onChange([...tags, ...formatInput(inputValue)])
        setInputValue('')
      }
      // Save currently edited tag
      else if (edit !== -1 && editValue !== '') {
        const tagsCopy = [...tags]
        tagsCopy.splice(edit, 1, ...formatInput(editValue))
        onChange(tagsCopy)
        setEdit(-1)
        setEditValue('')

        if (inputRef.current !== null) {
          inputRef.current.focus()
        }
      }
    } else if (e.key === 'Backspace') {
      // Delete last tag if no value has been inserted by user and no other tags are in edit
      if (inputValue === '' && edit === -1) {
        const tagsCopy = [...tags]
        const tag = tagsCopy.pop()
        if (typeof tag === 'string') {
          setInputValue(tag as string)
        }
        onChange(tagsCopy)
      }
      // Delete currently edited tag if empty
      if (edit !== -1 && editValue === '') {
        const tagsCopy = [...tags]
        tagsCopy.splice(edit, 1)
        setEdit(-1)
        setEditValue('')
        setFocus(true)
        onChange(tagsCopy)
      }
    }
  }

  function getCaretPosition() {
    if (window.getSelection && window.getSelection().getRangeAt) {
      const range = window.getSelection().getRangeAt(0)
      return range.startOffset
    }
  }

  function inputChange(e) {
    setInputValue(e.target.value)
  }

  function editTagChange(e) {
    setCaretPos(getCaretPosition)
    setEditValue(e.target.textContent)
  }

  function editOnClick() {
    setCaretPos(getCaretPosition)
  }

  function searchChange(e) {
    setSearch(e.target.value)
  }

  function toggleFocus() {
    setFocus(!focus)
  }

  function setEditable(index) {
    return function(e) {
      setEdit(index)
      setEditValue(e.target.textContent)
      setFocus(false)
    }
  }

  function editTagBlur() {
    setFocus(true)
    const tagsCopy = [...tags]

    tagsCopy.splice(edit, 1, ...formatInput(editValue))

    onChange(tagsCopy)
    setEdit(-1)
    setEditValue('')
  }

  function addTagOnInputBlur() {
    if (inputValue !== '') {
      onChange([...tags, ...formatInput(inputValue)])
      setInputValue('')
    }
  }

  function tagValues() {
    return Object.keys(tagsList)
      .map(function(key) {
        return { key, value: tagsList[key].value }
      })
      .filter(
        tag =>
          tag.key.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
          t(tag.value)
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()),
      )
  }

  function setInputRef(ref) {
    inputRef.current = ref
    if (inputRef.current !== null && focus) {
      inputRef.current.focus()
    }
  }

  function setFocusToInput() {
    if (inputRef.current !== null && focus) {
      inputRef.current.focus()
    }
  }

  function renderTrack() {
    return <div />
  }

  return (
    <Wrapper
      style={disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }}
    >
      <LockWrapper disabled={disabled}>
        <Header size={size}>
          <Tags onClick={setFocusToInput} className={`${className}-container`}>
            {tags.map((tag, index) => {
              return isObjectLike(tag) ? (
                <TagWrapper size={size} key={index}>
                  <div className={`${className}-tag-${index}`}>
                    <Trans i18nKey={(tag as ITag).value}>
                      {(tag as ITag).key}
                    </Trans>
                  </div>
                  <TagRemoveWrapper
                    size={size}
                    onClick={removeTag(index)}
                    className={`${className}-tag-${index}-button-remove`}
                  >
                    <FiX size={`${16 * size}px`} />
                  </TagRemoveWrapper>
                </TagWrapper>
              ) : (
                <TextTag
                  size={size}
                  suppressContentEditableWarning={true}
                  contentEditable={true}
                  key={index}
                  onFocus={setEditable(index)}
                  onBlur={editTagBlur}
                  onKeyDown={onKeyDown}
                  onInput={editTagChange}
                  onClick={editOnClick}
                  style={{ minWidth: `${5 * size}px` }}
                  className={`${className}-tag-${index}`}
                >
                  {tag as string}
                </TextTag>
              )
            })}

            <StyledInput
              disabled={disabled}
              size={size}
              ref={setInputRef}
              onBlur={addTagOnInputBlur}
              value={inputValue}
              onChange={inputChange}
              onKeyDown={onKeyDown}
              className={`${className}-input-tag`}
            />
          </Tags>
          <SearchWrapper size={size}>
            <FiSearch size={`${16 * size}px`} />
            <SearchInput
              disabled={disabled}
              size={size}
              type={'text'}
              placeholder={t('search_title')}
              value={search}
              onFocus={toggleFocus}
              onBlur={toggleFocus}
              onChange={searchChange}
              className={`${className}-input-search`}
            />
          </SearchWrapper>
        </Header>

        <TagsList size={size}>
          <Scrollbars renderTrackHorizontal={renderTrack}>
            {tagValues().map((tag, index) => (
              <ListTag size={size} key={tag.key} onClick={addTag(tag)}>
                <div className={`${className}-tag_list-${index}`}>
                  <Trans i18nKey={tag.value}>{tag.key}</Trans>
                </div>
              </ListTag>
            ))}
          </Scrollbars>
        </TagsList>
        {disabled && <img src={DisabledIcon2} alt={'disabled icon'} />}
      </LockWrapper>
    </Wrapper>
  )
}

export default withTranslation()(TagInput)

export interface ITagInputProps extends IFormElementProps {
  tagsList: ITagsForEvents & ITagsForEventsWithPreferences
  value?: string
  size?: number
  className?: string

  disabled?: boolean
  pinFirstTagKey?: string
}

export interface ITagInputDispatch {
  onChange: (value: (ITag | string)[]) => void
}

export interface ITag {
  value: string
  key: string
}
